/**
 * API utilities for RSSio
 */

interface APIOptions {
  retries?: number;
}

// API wrapper with error handling and retries
export const API = {
  async get<T = any>(url: string, options: APIOptions = {}): Promise<T> {
    const maxRetries = options.retries || 0;
    let lastError: Error | undefined;

    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        const response = await fetch(url, { credentials: "include" });
        if (!response.ok) {
          const err = new Error(`API error: ${response.status} ${response.statusText}`) as Error & { status: number };
          err.status = response.status;
          throw err;
        }
        return response.json();
      } catch (error) {
        lastError = error as Error;
        if (attempt < maxRetries) {
          await new Promise<void>((resolve) =>
            setTimeout(resolve, 100 * (attempt + 1)),
          );
        }
      }
    }
    throw lastError;
  },

  async post<T = any>(url: string, data?: unknown): Promise<T> {
    const response = await fetch(url, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const err = new Error(`API error: ${response.status} ${response.statusText}`) as Error & { status: number };
      err.status = response.status;
      throw err;
    }
    return response.json() as Promise<T>;
  },
};

export interface FeedsBatchResponse {
  feeds: Record<string, import("./state.ts").Article[]>;
  errors: Record<string, string>;
}

// Deduplication for single feed requests
const pendingFeedRequests = new Map<string, Promise<import("./state.ts").Article[]>>();

// Deduplication for batch feed requests
const pendingBatchRequests = new Map<string, Promise<FeedsBatchResponse>>();

export async function fetchFeedsBatch(
  urls: string[],
  forceRefresh = false,
): Promise<FeedsBatchResponse> {
  const uniqueUrls = Array.from(
    new Set(urls.map((u) => String(u || "").trim()).filter(Boolean)),
  );
  if (uniqueUrls.length === 0) {
    return { feeds: {}, errors: {} };
  }

  const sortedKey = `${uniqueUrls.slice().sort().join("|")}:${forceRefresh}`;
  if (pendingBatchRequests.has(sortedKey)) {
    return pendingBatchRequests.get(sortedKey)!;
  }

  const endpoint = forceRefresh ? "/feeds/fetch-batch?refresh=true" : "/feeds/fetch-batch";
  const fetchPromise = fetch(endpoint, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ urls: uniqueUrls, refresh: forceRefresh }),
  })
    .then((response) => {
      if (!response.ok) {
        return response.text().then((text) => {
          throw new Error(text || response.statusText);
        });
      }
      return response.json() as Promise<FeedsBatchResponse>;
    })
    .finally(() => {
      pendingBatchRequests.delete(sortedKey);
    });

  pendingBatchRequests.set(sortedKey, fetchPromise);
  return fetchPromise;
}

// Clear deduplication maps for testing
export function clearPendingRequestsForTesting(): void {
  pendingFeedRequests.clear();
  pendingBatchRequests.clear();
}

export async function fetchFeedWithDedup(url: string, forceRefresh = false): Promise<import("./state.ts").Article[]> {
  const cacheKey = `${url}:${forceRefresh}`;
  // If there's already a pending request for this URL with this refresh mode, wait for it
  if (pendingFeedRequests.has(cacheKey)) {
    return pendingFeedRequests.get(cacheKey)!;
  }

  const endpoint = forceRefresh ? "/feeds/fetch?refresh=true" : "/feeds/fetch";
  // Create the fetch promise
  const fetchPromise = fetch(endpoint, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ url }),
  })
    .then((response) => {
      if (!response.ok) {
        return response.text().then((text) => {
          throw new Error(text || response.statusText);
        });
      }
      return response.json();
    })
    .finally(() => {
      pendingFeedRequests.delete(cacheKey);
    });

  // Store the promise for deduplication
  pendingFeedRequests.set(cacheKey, fetchPromise);

  return fetchPromise;
}
